import { type IntlShape, useIntl } from "react-intl";
import { useMemo } from "react";
import { type ApolloClient, gql, type TypedDocumentNode } from "@apollo/client";
import type { GQL } from "@binale-tech/shared";
import { validateIban } from "@ui-components/Inputs/IbanInput";

export type Rule = {
    required?: boolean;
    message?: string;
    type?: string;
    max?: number;
    min?: number;
    pattern?: RegExp;
    validator?: (rule: unknown, value: string, callback?: unknown) => Promise<void>;
};
export type RuleObject = Rule;

export const validateStnrQuery: TypedDocumentNode<Pick<GQL.IQuery, "validateStnr">, GQL.IQueryValidateStnrArgs> = gql`
    query validateStnr($input: CompanyTaxInput!) {
        validateStnr(input: $input) {
            isValid
            fullTaxNumber
            error
            formattedTaxNumber
        }
    }
`;

export const validateVatQuery: TypedDocumentNode<Pick<GQL.IQuery, "validateVat">, GQL.IQueryValidateVatArgs> = gql`
    query validateVat($input: CompanyVatInput!, $companyId: String!) {
        validateVat(companyId: $companyId, input: $input) {
            isValid
            error
        }
    }
`;

// Wirtschafts-Identifikationsnummer. No companyId: unlike validateVat this is a stateless
// ERiC format check (structure + check digit), not a BZSt lookup on a company's behalf.
export const validateWidnrQuery: TypedDocumentNode<
    Pick<GQL.IQuery, "validateWidnr">,
    GQL.IQueryValidateWidnrArgs
> = gql`
    query validateWidnr($wIdNr: String!) {
        validateWidnr(wIdNr: $wIdNr) {
            isValid
            error
        }
    }
`;

interface IValidationCommon {
    intl: IntlShape;
    max: number;
    min: number;
}

const commonValidation = {
    required: (intl: IntlShape) => ({
        required: true,
        message: intl.formatMessage({ id: "app.validation.error.field_empty" }),
    }),
    max: (intl: IntlShape, max: number) => ({
        max,
        message: "max." + intl.formatMessage({ id: "app.validation.error.strictLength" }, { length: max }),
    }),
    min: (intl: IntlShape, min: number) => ({
        min,
        message: "min. " + intl.formatMessage({ id: "app.validation.error.strictLength" }, { length: min }),
    }),
    onlyNumber: { pattern: /^\d+$/, message: "should be a number" },
};

export const validation = {
    ...commonValidation,
    strict: (intl: IntlShape, count: number) => ({
        min: count,
        message: intl.formatMessage({ id: "app.validation.error.strictLength" }, { length: count }),
    }),
    email: (intl: IntlShape): Rule => ({
        type: "email",
        message: intl.formatMessage({ id: "app.validation.error.invalid_email" }),
    }),
    commonNumber: ({ intl, max, min }: IValidationCommon): Rule[] => {
        return [commonValidation.max(intl, max), commonValidation.min(intl, min), commonValidation.onlyNumber];
    },
    unique: (itemList: string[], initial: string | null): Rule => {
        return {
            message: "should be unique",
            validator: (rule, value: string) => {
                if (itemList.includes(value)) {
                    if (initial === undefined || initial === null || initial !== value) {
                        return Promise.reject("not unique");
                    }
                }
                return Promise.resolve();
            },
        };
    },
    inRange: (min: number, max: number): Rule => {
        return {
            message: `should be in range [${min}, ${max}]`,
            validator: (rule, value: string) => {
                if (value) {
                    if (+value < +min) {
                        return Promise.reject("too low");
                    }
                    if (+value > +max) {
                        return Promise.reject("too high");
                    }
                }
                return Promise.resolve();
            },
        };
    },
    pattern: (pattern: RegExp, message: string): Rule => {
        return {
            message,
            validator: (rule, value: string) => {
                if (value && !pattern.test(value)) {
                    return Promise.reject("pattern");
                }
                return Promise.resolve();
            },
        };
    },
    stnr: (client: ApolloClient, bundesland: string): RuleObject => {
        return {
            message: "Invalid StNr",
            validator: async (rule, value: string) => {
                if (!bundesland) {
                    return Promise.resolve();
                }
                if (!value) {
                    return Promise.resolve();
                }
                const res = await client
                    .query({
                        query: validateStnrQuery,
                        fetchPolicy: "no-cache",
                        variables: {
                            input: {
                                stnr: value,
                                bundesland,
                            },
                        },
                    })
                    .then((v) => {
                        return v.data.validateStnr?.isValid;
                    })
                    .catch(() => false);
                if (res) {
                    return Promise.resolve();
                }
                return Promise.reject();
            },
        };
    },
    vat: (client: ApolloClient, countryCode: string, companyId: string): RuleObject => {
        return {
            // message: "Invalid VAT",
            validator: async (rule, value: string) => {
                if (!countryCode) {
                    return Promise.resolve();
                }
                if (countryCode && !value) {
                    return Promise.reject(new Error("Bitte füllen oder leeren Sie beide Felder"));
                }
                if (!value) {
                    return Promise.resolve();
                }
                const res = await client
                    .query({
                        query: validateVatQuery,
                        fetchPolicy: "no-cache",
                        variables: {
                            companyId,
                            input: {
                                vatNumber: String(value),
                                countryCode,
                            },
                        },
                    })
                    .then((v) => {
                        return v.data.validateVat;
                    })
                    .catch(() => ({ isValid: false, error: "Invalid VAT, request error" }));
                if (res.isValid) {
                    return Promise.resolve();
                }
                return Promise.reject(new Error(res.error));
            },
        };
    },
    iban: (client: ApolloClient): RuleObject => {
        return {
            message: "IBAN ist ungültig",
            validator: async (rule, value: string) => {
                if (!value) {
                    return Promise.resolve();
                }
                const isValid = await validateIban(client, value);

                if (isValid === true) {
                    return Promise.resolve();
                }
                return Promise.reject(isValid);
            },
        };
    },
};

// interface RuleProp {}
// for React-Hook-Form
export const useValidationReactHookForm = () => {
    const intl = useIntl();

    const validator = useMemo(() => {
        return {
            required: {
                message: intl.formatMessage({ id: "app.validation.error.field_empty" }),
                value: true,
            },
            minLength: (min: number) => ({
                message: "min." + intl.formatMessage({ id: "app.validation.error.strictLength" }, { length: min }),
                value: min,
            }),
            maxLength: (max: number) => ({
                message: "max." + intl.formatMessage({ id: "app.validation.error.strictLength" }, { length: max }),
                value: max,
            }),
            strict: (count: number) => ({
                message: intl.formatMessage({ id: "app.validation.error.strictLength" }, { length: count }),
                value: count,
            }),
            onlyNumber: {
                message: "should be a number",
                value: /^\d+$/,
            },
            unique:
                (itemList: string[] = [], initial: string | null) =>
                (val: string) => {
                    if (val && initial !== val && itemList.includes(val)) {
                        return "not unique";
                    }
                    return true;
                },
            email: (val: string) => {
                const emailPattern =
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                if (!val || emailPattern.test(val)) {
                    return true;
                }
                return intl.formatMessage({ id: "app.validation.error.invalid_email" });
            },
        };
    }, [intl]);

    return validator;
};
